import { create } from 'zustand';
import type { SentimentEventData, MacroCalendarEvent, SentimentFactorData } from '../api/types';

const MAX_EVENTS = 200;
/** Per-symbol factor history depth for 046 delta math (newest first). */
const MAX_FACTOR_HISTORY = 8;
const EMPTY_EVENTS: SentimentEventData[] = [];
const EMPTY_CALENDAR: MacroCalendarEvent[] = [];

const EMPTY_FACTORS = new Map<string, SentimentFactorData>();
const EMPTY_HISTORY = new Map<string, SentimentFactorData[]>();

type SentimentState = {
  /** Recent sentiment events, newest first */
  events: SentimentEventData[];
  /** Upcoming macro calendar events, sorted by date */
  calendar: MacroCalendarEvent[];
  /** Per-symbol sentiment factor data, keyed by symbol */
  factors: Map<string, SentimentFactorData>;
  /** Per-symbol factor history (last N snapshots, newest first) — powers 046 deltas. */
  factorHistory: Map<string, SentimentFactorData[]>;
  /** IDs of events dismissed by the operator */
  dismissedIds: Set<string>;
  /** Monotonic version counter — bumped on change */
  version: number;
  /** When the last event was received */
  lastUpdateAt: number | null;
  /** Add a single event, dedupe by id, keep max 200 */
  addEvent: (event: SentimentEventData) => void;
  /** Bulk set from snapshot */
  setEvents: (events: SentimentEventData[]) => void;
  /** Set calendar events from snapshot */
  setCalendar: (calendar: MacroCalendarEvent[]) => void;
  /** Upsert a single factor by symbol */
  setFactor: (factor: SentimentFactorData) => void;
  /** Bulk set factors from snapshot */
  setFactors: (factors: SentimentFactorData[]) => void;
  /** Get factor for a specific symbol */
  getFactor: (symbol: string) => SentimentFactorData | undefined;
  /** Dismiss an event (operator override) */
  dismissEvent: (id: string) => void;
};

function pushHistory(
  history: Map<string, SentimentFactorData[]>,
  factor: SentimentFactorData,
): Map<string, SentimentFactorData[]> {
  const next = new Map(history);
  const existing = next.get(factor.symbol) ?? [];
  // Only record a new entry when the timestamp moved forward — protects against
  // rebroadcast bursts inflating history with duplicate points.
  if (existing.length > 0 && existing[0]!.updatedAt === factor.updatedAt) {
    next.set(factor.symbol, [factor, ...existing.slice(1)]);
    return next;
  }
  const updated = [factor, ...existing].slice(0, MAX_FACTOR_HISTORY);
  next.set(factor.symbol, updated);
  return next;
}

export const useSentimentStore = create<SentimentState>()((set) => ({
  events: EMPTY_EVENTS,
  calendar: EMPTY_CALENDAR,
  factors: EMPTY_FACTORS,
  factorHistory: EMPTY_HISTORY,
  dismissedIds: new Set<string>(),
  version: 0,
  lastUpdateAt: null,

  addEvent: (event) =>
    set((state) => {
      // Dedupe by id
      if (state.events.some((e) => e.id === event.id)) {
        return {};
      }
      const next = [event, ...state.events];
      if (next.length > MAX_EVENTS) next.length = MAX_EVENTS;
      return {
        events: next,
        version: state.version + 1,
        lastUpdateAt: Date.now(),
      };
    }),

  setEvents: (events) =>
    set((state) => {
      // Dedupe by id
      const seen = new Set<string>();
      const deduped: SentimentEventData[] = [];
      for (const e of events) {
        if (!seen.has(e.id)) {
          seen.add(e.id);
          deduped.push(e);
        }
      }
      // Sort newest first
      deduped.sort((a, b) => b.ts - a.ts);
      if (deduped.length > MAX_EVENTS) deduped.length = MAX_EVENTS;
      return {
        events: deduped.length === 0 ? EMPTY_EVENTS : deduped,
        version: state.version + 1,
        lastUpdateAt: Date.now(),
      };
    }),

  setCalendar: (calendar) =>
    set(() => ({
      calendar: calendar.length === 0 ? EMPTY_CALENDAR : calendar,
    })),

  setFactor: (factor) =>
    set((state) => {
      const next = new Map(state.factors);
      next.set(factor.symbol, factor);
      return {
        factors: next,
        factorHistory: pushHistory(state.factorHistory, factor),
        version: state.version + 1,
        lastUpdateAt: Date.now(),
      };
    }),

  setFactors: (factors) =>
    set((state) => {
      if (factors.length === 0) {
        return { factors: EMPTY_FACTORS, version: state.version + 1, lastUpdateAt: Date.now() };
      }
      const next = new Map<string, SentimentFactorData>();
      let history = state.factorHistory;
      for (const f of factors) {
        next.set(f.symbol, f);
        history = pushHistory(history, f);
      }
      return {
        factors: next,
        factorHistory: history,
        version: state.version + 1,
        lastUpdateAt: Date.now(),
      };
    }),

  getFactor: (symbol): SentimentFactorData | undefined => useSentimentStore.getState().factors.get(symbol),

  dismissEvent: (id) =>
    set((state) => {
      const next = new Set(state.dismissedIds);
      next.add(id);
      return { dismissedIds: next, version: state.version + 1 };
    }),
}));
